
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import AddRoomPopup from '@/components/popup/common/AddRoomPopup.vue';
import RemoveRoomOrSpacePopup from '@/components/popup/common/RemoveRoomOrSpacePopup.vue';
import RoomsMemberListItem from './RoomsMemberListItem.vue';
import SubRooomListItem from './SubRooomListItem.vue';
import {
  UserRoomStateData,
  Room,
  RoomSpace,
  SubRoom,
  User,
  UserRoomState,
  RoomActivityLogWithUser,
  UserProperties,
} from '@/utils/models';
import RoomsMemberListItemCollapsed from './RoomsMemberListItemCollapsed.vue';
import RoomsMemberBySubrooms from './RoomsMemberBySubrooms.vue';
import { formatTime } from '@/utils/functions';
import { GeneralSettingsSnapshot, SettingsSnapshot } from '@/utils/models';

@Component({
  components: {
    RemoveRoomOrSpacePopup,
    AddRoomPopup,
    RoomsMemberListItem,
    SubRooomListItem,
    RoomsMemberListItemCollapsed,
    RoomsMemberBySubrooms,
  },
})
export default class RoomsMemberList extends Vue {
  @Prop() private user!: User;
  @Prop() space!: RoomSpace;
  @Prop() room!: Room;
  @Prop() private userStateData!: UserRoomStateData[];
  @Prop() isConferenceEditMode!: boolean;
  @Prop() isListCollapsed!: boolean;
  @Prop() private state!: UserRoomState;
  @Prop() roomActivities!: RoomActivityLogWithUser[];

  get userData(): User {
    return this.userState?.user || this.user;
  }
  get userState(): UserRoomState {
    return this.state;
  }
  get filteredRoomActivities(): RoomActivityLogWithUser[] {
    if (this.selectedFilters.length === 0) {
      return [];
    }
    return this.roomActivities.filter((activity) => {
      return this.selectedFilters.some((filter) => {
        if (filter === 'メッセージ') {
          return activity.log.eventType === 'userMessage' && activity.log.note;
        } else if (filter === '入退室') {
          return activity.log.eventType === 'enter room' || activity.log.eventType === 'leave room';
        } else if (filter === '声かけ') {
          return activity.log.eventType === 'knock user' && activity.targetDisplayName;
        }
      });
    });
  }
  @Emit() changeCollapsed(collapsed: boolean) {}

  tabs = ['マイリスト', '一覧', '履歴'];
  selectedTab = this.tabs[0];
  filters: string[] = ['メッセージ', '入退室', '声かけ'];
  selectedFilters: string[] = ['メッセージ', '入退室', '声かけ'];
  selectedSubroom: SubRoom | null = null;
  memberSearchWord: string = '';

  get myList() {
    let list = this.user?.myList ?? [];
    let plist = this.userStateData ?? [];
    return list.map((u) => {
      return { user: u, state: plist.find((s) => s.userState.userId == u.id)?.userState };
    });
  }

  get members() {
    return this.room?.roomStates ?? [];
  }
  get searchedMembers() {
    let searchedMembers: UserRoomState[] = [];
    this.members.forEach((state) => {
      if (
        state.user.name.toLocaleLowerCase().includes(this.memberSearchWord) ||
        state.user.email?.toLocaleLowerCase().includes(this.memberSearchWord.toLocaleLowerCase())
      ) {
        searchedMembers.push(state);
      }
    });
    return searchedMembers;
  }

  get searchedMyself() {
    let searchedMyself: UserRoomState[] = [];
    if (this.user && this.members?.length) {
      this.members.forEach((state) => {
        if (typeof state === 'object' && state.user?.id.includes(this.user.id)) {
          searchedMyself.push(state);
        }
      });
    }
    return searchedMyself;
  }

  mounted() {
    if (this.isConferenceEditMode) {
      this.tabs = ['会議室'];
      this.changeTab('会議室');
    } else {
      this.setValuesAccordingToStore();
    }
  }
  changeTab(tab: string) {
    this.selectedTab = tab;
    this.subroomSelectionChanged();
    this.$store.commit('SetShowSubroomArea', this.selectedTab == '会議室');
    this.applyNewSettings();
  }
  changeFilter(filter: string) {
    const index = this.selectedFilters.indexOf(filter);
    if (index >= 0) {
      this.selectedFilters.splice(index, 1);
    } else {
      this.selectedFilters.push(filter);
    }
    this.applyNewSettings();
  }
  onClickMember(event: { rect: any; userId: string }): void {
    let ev: { rect: any; userOnMyList: { user: User; isExitUser: boolean; allowed: boolean } };
    const member = this.members.find((m) => m.user.id === event.userId);
    /* From 一覧 */
    if (member) {
      ev = {
        rect: event.rect,
        userOnMyList: {
          user: member.user,
          isExitUser: true,
          allowed: member.allowed,
        },
      };
    } else {
      /* From マイリスト */
      const userState = this.myList.find((us) => us.user.id === event.userId)!;
      const isExitUser = !!userState.state;
      ev = {
        rect: event.rect,
        userOnMyList: {
          user: userState.user,
          isExitUser: isExitUser,
          allowed: isExitUser ? userState.state!.allowed : false,
        },
      };
    }
    this.displayUserMenuOnList(ev);
  }
  onSubroomSelectionChanged(ev?: { rect: any; subroom: SubRoom }) {
    this.subroomSelectionChanged(ev);
    this.selectedSubroom = ev?.subroom ?? null;
  }

  memberListOpened = true;
  toggleMemberList() {
    this.memberListOpened = !this.memberListOpened;
  }
  openMemberList() {
    if (!this.memberListOpened) {
      this.memberListOpened = true;
    }
  }
  @Watch('selectedTab')
  @Watch('filteredRoomActivities')
  scrollDown(): void {
    if (this.selectedTab === '履歴') {
      this.$nextTick(() => {
        const element = this.$refs.activityList as HTMLDivElement;
        if (element) {
          element.scrollTo({
            top: element.scrollHeight,
          });
        }
      });
    }
  }
  formatTime(date: Date | number): string {
    return formatTime(date);
  }
  retrieveUserNameFirstLetter(roomActivity: RoomActivityLogWithUser): string {
    return roomActivity?.user.name.charAt(0) ?? '';
  }
  private setValuesAccordingToStore(): void {
    const properties: UserProperties = this.$store.state.userProperties;
    this.selectedTab = properties.lastSelectedRoomInfoTab;
    this.selectedFilters = properties.lastSelectedRoomHistoryFilters;
  }
  @Emit()
  private applyNewSettings(): Partial<GeneralSettingsSnapshot> {
    return {
      properties: {
        lastSelectedRoomInfoTab: this.selectedTab,
        lastSelectedRoomHistoryFilters: this.selectedFilters,
      },
    };
  }
  @Emit()
  displayUserMenuOnList(ev: { rect: any; userOnMyList: { user: User; isExitUser: boolean; allowed: boolean } }) {}
  @Emit()
  onClickBellButton() {}
  @Emit()
  subroomSelectionChanged(ev?: { rect: any; subroom: SubRoom }) {}
  @Emit()
  editSubroom(subroom: SubRoom) {}
  @Emit()
  deleteSubroom(subroom: SubRoom) {}
}
